import React, { useState, useRef } from "react";
import { config } from "../../../utils/config";
import {
  createObjectFromArrayOfObjects,
  disabledIfFieldChecker,
} from "../../../utils/helperFuctions";

function InputFile({
  label,
  name,
  id,
  defaultValue,
  handleChange,
  handleDeleteFiles,
  required,
  disabled,
  currentFields,
  disabledIfField,
}) {
  const [files, setFiles] = useState(initFiles(defaultValue));
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const fileInputRef = useRef(null);

  //init Files
  // Function to add a file to the list
  function initFiles(defaultValue) {
    if (defaultValue && Array.isArray(defaultValue)) {
      const defArr = defaultValue.map((file) => {
        const isImage = file.type?.match("image.*");
        const objectURL = `${config.apiUrl}/assets/${file.name}`;

        return {
          name: file.original_name,
          assetName: file.name,
          size: file.size,
          url: objectURL,
          isImage: isImage,
          id: file.id,
        };
      });

      return createObjectFromArrayOfObjects(defArr, "url");
    }

    return {};
  }

  // Function to add a file to the list
  function addFile(file) {
    const isImage = file.type.match("image.*");
    const objectURL = URL.createObjectURL(file);

    // Set the maximum file size limit (in bytes)
    const maxFileSize = 10 * 1024 * 1024; // 10 MB

    if (file?.size > maxFileSize) {
      alert("File size exceeds the limit of 10MB. Maximum 10MB");
      // Clear the file input
      return;
    }

    const newFile = {
      name: file.name,
      size: file.size,
      url: objectURL,
      isImage: isImage,
    };

    setFiles((prevFiles) => ({
      ...prevFiles,
      [objectURL]: newFile,
    }));
  }

  // Function to handle file drop
  function dropHandler(event) {
    event.preventDefault();
    for (const file of event.dataTransfer.files) {
      addFile(file);
    }
    setIsDraggedOver(false);
    handleChange(event);
  }

  // Function to handle file selection using the input element
  function handleFileSelect(event) {
    for (const file of event.target.files) {
      addFile(file);
    }

    handleChange(event);
  }
  const openImagePreview = (e, objectURL) => {
    // Open the preview image in a new window or tab
    if (e) e.preventDefault();

    window.open(objectURL);
  };

  // Function to handle file deletion
  function deleteFile(url) {
    const newFiles = { ...files };
    const deletedFile = newFiles[url];
    delete newFiles[url];
    //Old Files
    const oldFiles = fileInputRef.current.files;

    const dataTransfer = new DataTransfer();

    const _newFiles = Object.values(newFiles);
    // Iterate over the selected files
    for (let i = 0; i < oldFiles.length; i++) {
      const file = oldFiles[i];
      let shouldKeep = _newFiles.some(
        (obj) => obj.name === file.name && obj.size === file.size
      );

      // If the file should be kept, add it to the new input element
      if (shouldKeep) {
        dataTransfer.items.add(file);
      }
    }

    setFiles(newFiles);

    const _e = {
      target: {
        name: fileInputRef.current.name,
        files: dataTransfer.files,
        type: fileInputRef.current.type,
      },
    };
    handleChange(_e);
    if (deletedFile.id) {
      handleDeleteFiles({
        name: deletedFile.assetName,
        id: deletedFile.id,
      });
    }
  }

  // Function to clear all selected files
  function clearFiles(e) {
    e.preventDefault();
    setFiles({});

    const dataTransfer = new DataTransfer();

    const _e = {
      target: {
        name: fileInputRef.current.name,
        files: dataTransfer.files,
        type: fileInputRef.current.type,
      },
    };

    handleChange(_e);
  }
  const isFieldDisabled =
    disabledIfField != null
      ? disabled ||
        disabledIfFieldChecker(currentFields, {
          field: disabledIfField.field,
          valuesArray: disabledIfField.isEqual,
        })
      : disabled ?? false;

  return (
    <div
      className={`h-full mt-2 ${isDraggedOver ? "draggedover" : ""}`}
      onDrop={() => {
        if (isFieldDisabled === true) return;
        dropHandler();
      }}
      onDragOver={(e) => {
        if (isFieldDisabled === true) return;
        e.preventDefault();
      }}
      onDragLeave={() => {
        if (isFieldDisabled === true) return;
        setIsDraggedOver(false);
      }}
      onDragEnter={() => {
        if (isFieldDisabled === true) return;
        setIsDraggedOver(true);
      }}
    >
      <label
        htmlFor={name}
        className="block text-sm font-medium leading-6 text-gray-900"
      >
        {label}
        {required ? "*" : ""}
      </label>
      <main className="container mx-auto max-w-screen-lg h-full">
        {/* file upload modal */}
        <article
          aria-label="File Upload Modal"
          className="relative h-full flex flex-col bg-white shadow-xl rounded-md"
        >
          {/* scroll area */}
          <section className=" overflow-auto p-8 w-full h-full flex flex-col">
            <header className="border-dashed border-2 border-gray-400 py-12 flex flex-col justify-center items-center">
              <p className="mb-3 font-semibold text-gray-900 flex flex-wrap justify-center">
                <span>Drag and drop your</span>&nbsp;
                <span>files anywhere or</span>
              </p>
              <input
                id={id ?? "hidden-input"}
                name={name}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                ref={fileInputRef}
              />
              <button
                id="button"
                className="mt-2 rounded-sm px-3 py-1 bg-gray-200 hover:bg-gray-300 focus:shadow-outline focus:outline-none"
                onClick={(e) => {
                  if (isFieldDisabled === true) return;
                  e.preventDefault();
                  fileInputRef.current.click();
                }}
              >
                Upload a file
              </button>
            </header>

            <h1 className="pt-8 pb-3 font-semibold sm:text-lg text-gray-900">
              Files
            </h1>

            {/* Modified gallery container for horizontal scrolling on mobile */}
            <ul
              id="gallery"
              className="flex flex-1 z-50 flex-wrap -m-1 
                         md:flex-wrap 
                         sm:flex-nowrap sm:overflow-x-auto sm:gap-2 sm:pb-4
                         max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:gap-2 max-sm:pb-4"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#CBD5E0 #F7FAFC",
                zIndex: 0,
              }}
            >
              {Object.keys(files).map((url) => (
                <li
                  key={url}
                  className="block p-1 
                           md:w-1/2 md:sm:w-1/3 md:md:w-1/4 md:lg:w-1/6 md:xl:w-1/8 md:h-24
                           sm:min-w-[120px] sm:w-[120px] sm:h-24 sm:flex-shrink-0
                           max-sm:min-w-[120px] max-sm:w-[120px] max-sm:h-24 max-sm:flex-shrink-0"
                >
                  <article
                    tabIndex="0"
                    className="group w-full h-full rounded-md focus:outline-none focus:shadow-outline elative bg-gray-100 cursor-pointer relative shadow-sm"
                  >
                    {files[url].url && (
                      <div
                        onClick={(e) => {
                          const link = document.createElement("a");
                          link.href = files[url].url;
                          link.target = "_blank";
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        <img
                          alt="upload preview"
                          className={`img-preview w-full h-full sticky object-cover rounded-md bg-fixed aspect-[3/1.5] ${
                            files[url].isImage ? "" : "hidden"
                          }`}
                          src={files[url].url}
                        />
                        <section className="flex flex-col rounded-md text-xs break-words w-full z-20 top-0 py-2 px-3">
                          <h1 className="flex-1 group-hover:text-blue-800 truncate">
                            {files[url].name}
                          </h1>
                          <div className="flex">
                            <span className="p-1 text-blue-800">
                              <i>
                                <svg
                                  className="fill-current w-4 h-4 ml-auto pt-1"
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M15 2v5h5v15h-16v-20h11zm1-2h-14v24h20v-18l-6-6z" />
                                </svg>
                              </i>
                            </span>
                            <p className="p-1 size text-xs text-gray-700">
                              {files[url].size > 1024
                                ? files[url].size > 1048576
                                  ? Math.round(files[url].size / 1048576) + "mb"
                                  : Math.round(files[url].size / 1024) + "kb"
                                : files[url].size + "b"}
                            </p>
                            <button
                              className="delete ml-auto focus:outline-none hover:bg-gray-300 p-1 rounded-md text-gray-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteFile(url);
                              }}
                            >
                              <svg
                                className="pointer-events-none fill-current w-4 h-4 ml-auto"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  className="pointer-events-none"
                                  d="M3 6l3 18h12l3-18h-18zm19-4v2h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.316c0 .901.73 2 1.631 2h5.711z"
                                />
                              </svg>
                            </button>
                          </div>
                        </section>
                      </div>
                    )}
                  </article>
                </li>
              ))}
            </ul>
          </section>

          {/* sticky footer */}
          <div className="flex justify-end px-8 py-4">
            {/* <button id="submit" className="rounded-sm px-3 py-1 bg-blue-700 hover:bg-blue-500 text-white focus:shadow-outline focus:outline-none">
              Upload now
            </button> */}
            {/* <button id="cancel" className="ml-3 rounded-sm px-3 py-1 hover:bg-gray-300 focus:shadow-outline focus:outline-none" onClick={clearFiles}>
              Clear files
            </button> */}
          </div>
        </article>
      </main>
    </div>
  );
}

export default InputFile;
